import { apiService } from '@/api/apiConfig';
import { ExpenseCategoryCreationData, ExpenseCategory } from './types/expense-category.types';

// Types for expense categories
export interface ExpenseCategoryData {
  name: string;
  description?: string;
  ecmev?: number;
  ecmev_alert_threshold?: number;
}

export interface ExpenseCategoryResponse {
  id: string;
  name: string;
  description?: string;
}

export interface ExpenseCategoriesResponse {
  data: ExpenseCategory[];
}

const handleExpenseCategoryError = (error: any): Error => {
  if (error.response?.data?.message) {
    return new Error(`Expense category operation failed: ${error.response.data.message}`);
  }
  return error instanceof Error ? error : new Error('Expense category operation failed');
};

export const expenseRevenueService = {
  getExpenseCategories: async (budgetId: string): Promise<ExpenseCategoriesResponse> => {
    try {
      return await apiService.request<ExpenseCategoriesResponse>({
        method: 'GET',
        url: `/budget/${budgetId}/expense-category`
      });
    } catch (error) {
      throw handleExpenseCategoryError(error);
    }
  },

  getExpenseCategory: async (budgetId: string, categoryId: string): Promise<any> => {
    try {
      return await apiService.request<ExpenseCategory>({
        method: 'GET',
        url: `/budget/${budgetId}/expense-category/${categoryId}`
      });
    } catch (error) {
      throw handleExpenseCategoryError(error);
    }
  },

  createExpenseCategory: async (
    budgetId: string,
    categoryData: ExpenseCategoryCreationData
  ): Promise<ExpenseCategory> => {
    try {
      const response = await apiService.request<{ data: ExpenseCategory }>({
        method: 'POST',
        url: `/budget/${budgetId}/expense-category`,
        data: categoryData,
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      throw handleExpenseCategoryError(error);
    }
  },

  activateExpenseCategories: async (budgetId: string, data: any): Promise<any> => {
    try {
      return await apiService.request<any>({
        method: 'POST',
        url: `/budget/${budgetId}/expense-category/activate`,
        data,
        headers: {
          'Content-Type': 'application/json'
        }
      });
    } catch (error) {
      throw handleExpenseCategoryError(error);
    }
  },

  deactivateExpenseCategory: async (budgetId: string, data: any): Promise<any> => {
    try {
      return await apiService.request<any>({
        method: 'POST',
        url: `/budget/${budgetId}/expense-category/deactivate`,
        data,
        headers: {
          'Content-Type': 'application/json'
        }
      });
    } catch (error) {
      throw handleExpenseCategoryError(error);
    }
  },

  updateExpenseCategory: async (
    budgetId: string,
    categoryId: string,
    categoryData: ExpenseCategoryData
  ): Promise<ExpenseCategoryResponse> => {
    try {
      return await apiService.request<ExpenseCategoryResponse>({
        method: 'PATCH',
        url: `/budget/${budgetId}/expense-category/${categoryId}`,
        data: categoryData,
        headers: {
          'Content-Type': 'application/json'
        }
      });
    } catch (error) {
      throw handleExpenseCategoryError(error);
    }
  },

  deleteExpenseCategory: async (budgetId: string, categoryId: string): Promise<void> => {
    try {
      return await apiService.request<void>({
        method: 'DELETE',
        url: `/budget/${budgetId}/expense-category/${categoryId}`
      });
    } catch (error) {
      throw handleExpenseCategoryError(error);
    }
  },

  adjustExpenseCategory: async (budgetId: string, ecId: string, data: any): Promise<any> => {
    try {
      return await apiService.request<any>({
        method: 'PATCH',
        url: `/budget/${budgetId}/expense-category/${ecId}/adjust-allocation`,
        data,
        headers: {
          'Content-Type': 'application/json'
        }
      });
    } catch (error) {
      throw handleExpenseCategoryError(error);
    }
  },

  adjustExpenseCategoriesAllocation: async (budgetId: string, data: any): Promise<any> => {
    try {
      return await apiService.request<any>({
        method: 'PATCH',
        url: `/budget/${budgetId}/expense-category/ecmev/adjust-allocation`,
        data,
        headers: {
          'Content-Type': 'application/json'
        }
      });
    } catch (error) {
      throw handleExpenseCategoryError(error);
    }
  }
};
